import { ChevronRightIcon, HeartIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as BagIcon } from '../../../../assets/tx_bag.svg';
import { ReactComponent as TxnChargeIcon } from '../../../../assets/tx_charge.svg';
import { ReactComponent as RefundIcon } from '../../../../assets/tx_refund.svg';
import { PaymentMethodLogo } from '../../../../components/PaymentMethodLogo';
import { DONATION_TONE } from '../../../../constants/donation.const';
import { TransactionStatus } from '../../../../constants/order.const';
import { transactionData } from '../../../../core/api/http-methods/get-user-transactions';
import { formatCurrency } from '../../../../utils/fiat-currency.util';
import { activityRowLine } from '../../../../utils/payment-method-view';

export type ActivityModalType = 'requestRefund' | 'reportDoubleCharge';

const getStatusDisplay = (transaction_status: string) => {
  switch (transaction_status) {
    case TransactionStatus.FAILED:
      return { text: 'Failed', className: 'text-red-600' };
    case TransactionStatus.SUCCESS:
    case 'accepted':
    case 'confirmed':
      return { text: 'Success', className: 'text-green-600' };
    case TransactionStatus.INITIATED:
    case 'pending':
      return { text: 'Pending', className: 'text-yellow-600' };
    default:
      return { text: transaction_status, className: 'text-gray-600' };
  }
};

type Props = {
  transaction: transactionData;
};

const TransactionIcon: React.FC<{ record_type: string }> = ({
  record_type
}) => {
  switch (record_type) {
    case 'order':
      return <BagIcon className="h-5 w-auto text-gray-500" />;
    case 'refund':
      return <RefundIcon className="h-5 w-auto text-gray-500" />;
    default:
      return <TxnChargeIcon className="h-5 w-auto text-gray-500" />;
  }
};

/** "Loyalty Recorded" for a loyalty-only order, else "via" + the payment method, if any. */
const MethodLine: React.FC<Props> = ({ transaction }) => {
  const line = activityRowLine(transaction);
  if (line.kind === 'none') return null;
  if (line.kind === 'loyalty')
    return <p className="text-sm text-gray-500">Loyalty Recorded</p>;
  const { text, logo } = line;
  if (!logo) return <p className="text-sm text-gray-500">{text}</p>;
  return (
    <p className="flex items-center gap-1.5 text-sm text-gray-500">
      <PaymentMethodLogo
        logo={logo}
        imgClassName="h-4 w-6 shrink-0 object-contain"
        glyphClassName="h-4 w-4 shrink-0 text-gray-400"
      />
      <span className="min-w-0">{text}</span>
    </p>
  );
};

const TransactionListItem: React.FC<Props> = ({ transaction }) => {
  const navigate = useNavigate();
  const statusDisplay = getStatusDisplay(transaction.status);

  const handleClick = () => {
    navigate(`/transactions/${transaction.id}`, {
      state: { transaction }
    });
  };

  return (
    <li className="bg-white transition-colors duration-200 hover:bg-gray-50">
      <button
        onClick={handleClick}
        className="w-full px-4 py-4 text-left focus:outline-none"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <TransactionIcon record_type={transaction.record_type} />
            <div>
              <time className="block text-sm text-gray-500">
                {dayjs(transaction?.date).format('DD MMM YYYY, HH:mm')}
              </time>
              <p className="text-sm font-medium text-gray-900">
                {transaction?.title}
              </p>
              <MethodLine transaction={transaction} />
              {transaction.includes_donation && (
                <span
                  className={clsx(
                    'mt-1.5 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                    DONATION_TONE.pill
                  )}
                >
                  <HeartIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Includes donation
                </span>
              )}
            </div>
          </div>

          <div className="flex min-w-fit items-center gap-3 text-right">
            <div>
              <div className="text-base font-semibold text-gray-900">
                {formatCurrency(
                  Number(transaction.amount),
                  transaction?.currency
                )}
              </div>
              <div
                className={clsx('text-sm font-medium', statusDisplay.className)}
              >
                {statusDisplay.text}
              </div>
            </div>
            <ChevronRightIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </button>
    </li>
  );
};

export default TransactionListItem;
